<template>
    <div class="refund-review-block">
        <div class="block-header">
            <h3 class="block-title">Review Your Refund Request</h3>
            <p class="block-description">
                Please review the details of your refund request before submitting.
            </p>
        </div>

        <div class="review-summary">
            <div class="info-card">
                <div class="card-header">
                    <svg class="w-6 h-6 mr-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zM8 6V5a2 2 0 114 0v1H8zm2 6a1 1 0 011 1v2a1 1 0 11-2 0v-2a1 1 0 011-1z" clip-rule="evenodd" />
                    </svg>
                    <h4 class="card-title">License Information</h4>
                </div>
                <div class="card-content">
                    <div class="license-details">
                        <div class="detail-row">
                            <span class="detail-label">License Type:</span>
                            <span class="detail-value">{{ license?.type }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">Billing Period:</span>
                            <span class="detail-value">{{ license?.term }} {{ license?.term === 1 ? 'month' : 'months' }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">Amount (est.):</span>
                            <span class="detail-value">{{ formatCurrency(license?.price) }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="info-card">
                <div class="card-header">
                    <svg class="w-6 h-6 mr-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                    </svg>
                    <h4 class="card-title">Details</h4>
                </div>
                <div class="card-content">
                    <div class="refund-details">
                        <div class="detail-row">
                            <span class="detail-label">Reason:</span>
                            <span class="detail-value">{{ getReasonText(feedback?.reason) }}</span>
                        </div>
                        <div v-if="feedback?.additionalFeedback" class="detail-row">
                            <span class="detail-label">Additional Details:</span>
                            <span class="detail-value">{{ feedback.additionalFeedback }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="info-card">
                <div class="card-header">
                    <svg class="w-6 h-6 mr-3 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                    <h4 class="card-title">What Happens Next</h4>
                </div>
                <div class="card-content">
                    <ul class="next-steps">
                        <li>Your license will be cancelled immediately</li>
                        <li>You'll receive a refund within 5-10 business days</li>
                        <li>You may lose access to some services</li>
                        <li>You can reactivate your account at any time</li>
                    </ul>
                </div>
            </div>
        </div>

        <div class="action-buttons">
            <button 
                class="btn btn-outline"
                @click="$emit('goToPage', 3)"
            >
                Back
            </button>
            <button 
                class="btn btn-error"
                @click="submitRefund"
                :disabled="isSubmitting"
            >
                {{ isSubmitting ? 'Submitting...' : 'Submit Refund Request' }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import useUtils from '~/composables/useUtils';

interface Props {
    license?: any;
    feedback?: any;
}

const { formatCurrency } = useUtils();

const props = defineProps<Props>();

const emit = defineEmits<{
    close: [];
    goToPage: [page: number];
    terminationPending: [amount: number];
    cancelAutoRenew: [];
}>();

const isSubmitting = ref(false);

const getReasonText = (reason: string) => {
    const reasons = {
        'too_expensive': 'Too expensive',
        'not_using': 'Not using the service',
        'better_alternative': 'Found a better alternative',
        'technical_issues': 'Technical issues',
        'customer_service': 'Customer service issues',
        'other': 'Other'
    };
    return reasons[reason as keyof typeof reasons] || reason;
};

const submitRefund = async () => {
    isSubmitting.value = true;
    
    try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Emit termination pending with refund amount
        const refundAmount = props.license?.amount || 0;
        emit('terminationPending', refundAmount);
    } catch (error) {
        console.error('Error submitting refund:', error);
    } finally {
        isSubmitting.value = false;
    }
};
</script>

<style lang="scss" scoped>
.refund-review-block {
    @apply p-6;
}

.block-header {
    @apply mb-6 text-center;
    
    .block-title {
        @apply text-2xl font-semibold text-gray-900 mb-2;
    }
    
    .block-description {
        @apply text-gray-600 text-base;
    }
}

.review-summary {
    @apply mb-6 space-y-4;
}

.info-card {
    @apply bg-white rounded-lg border border-gray-200 p-6;
    
    .card-header {
        @apply flex items-center mb-4;
        
        .card-title {
            @apply text-lg font-semibold text-gray-900;
        }
    }
    
    .card-content {
        .license-details,
        .refund-details {
            .detail-row {
                @apply flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0;
                
                .detail-label {
                    @apply font-medium text-gray-700;
                }
                
                .detail-value {
                    @apply text-gray-900 text-right;
                }
            }
        }
        
        .next-steps {
            @apply list-disc list-inside space-y-2 text-gray-700;
        }
    }
}

.action-buttons {
    @apply flex justify-center gap-3;
    
    .btn {
        @apply px-6 py-2 rounded-lg font-medium transition-all duration-200;
        
        &.btn-outline {
            @apply border border-gray-300 text-gray-700 bg-white;
            
            &:hover:not(:disabled) {
                @apply border-gray-400 bg-gray-50;
            }
            
            &:disabled {
                @apply opacity-50 cursor-not-allowed;
            }
        }
        
        &.btn-error {
            @apply bg-red-500 text-white border border-red-500;
            
            &:hover:not(:disabled) {
                @apply bg-red-600 border-red-600;
            }
            
            &:disabled {
                @apply opacity-50 cursor-not-allowed;
            }
        }
    }
}
</style> 